import { useState } from 'react'
import domPurify from 'dompurify'
import moment, { Moment } from 'moment'
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined'
import {
  Button,
  TextField,
  Select,
  FormControl,
  MenuItem,
  Tooltip,
  Checkbox,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  Modal,
} from '@resiliatech/birdsong'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import { DatePicker } from 'Components/SharedComponents/DatePicker/DatePicker'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  AddGoalInput,
  GoalCategory,
  GoalType,
  Goal,
  GoalUpdateInput,
  GoalImpactCategory,
} from 'Generated/graphql-types'
import useNonprofitOrgTeam from 'Hooks/useNonprofitOrgTeam/useNonprofitOrgTeam'
import { categories, impactCategories } from 'TypeDefs/Goal'
import { analytics } from 'Modules/Analytics'
import { initiatives } from 'Modules/Analytics/trackActionInterface/Impact/Initiatives'
import { goals } from 'Modules/Analytics/trackActionInterface/Impact/Goals'
import {
  Buttons,
  DatePickers,
  GoalModalContainer,
  Header,
  Inputs,
  ShareFunderText,
  FunderCheckBox,
  UnshareWarning,
  TooltipText,
  TooltipBox,
  CategorySelector,
} from './GoalModal.styled'
import { GOAL_DATE_FORMAT } from '../types'

type GoalModalProps = {
  isOpen: boolean
  onClose: () => void
  onCreateGoal?: (goal: AddGoalInput) => Promise<void>
  goal?: Goal
  onUpdateGoal?: (goal: GoalUpdateInput) => Promise<void>
}

export const GoalModal = ({
  isOpen,
  onClose,
  onCreateGoal,
  goal,
  onUpdateGoal,
}: GoalModalProps) => {
  const {
    nonprofitOrgConfig: {
      orgId: nonprofitOrgId,
      platformPlan,
      role,
      nonprofitOrgRole,
      activeFunders,
    },
  } = useAppState()
  const { teamOptions } = useNonprofitOrgTeam()
  const [title, setTitle] = useState(goal?.title || '')
  const [description, setDescription] = useState(goal?.description || '')
  const [impactCategory, setImpactCategory] =
    useState<GoalImpactCategory | null>(
      goal?.impactCategory || GoalImpactCategory.Mission
    )
  const [category, setCategory] = useState<GoalCategory>(
    goal?.category || GoalCategory.None
  )
  const [ownerId, setOwnerId] = useState<number | null>(goal?.owner?.id || null)
  const [startDate, setStartDate] = useState<Moment | null>(
    goal?.startDate ? moment.utc(goal?.startDate) : null
  )
  const [endDate, setEndDate] = useState<Moment | null>(
    goal?.endDate ? moment.utc(goal?.endDate) : null
  )
  const [isCategorySelectOpen, setIsCategorySelectOpen] = useState(false)
  const [isOwnerSelectOpen, setIsOwnerSelectOpen] = useState(false)
  const funderSharedGoal = goal?.FunderOrgToSharedGoal.find(
    (sharedGoal) => sharedGoal.goalId === goal?.id && !sharedGoal.archivedAt
  )

  const [isSharedWithFunder, setIsSharedWithFunder] = useState(
    !!funderSharedGoal
  )

  const hasActiveFunders = activeFunders && activeFunders.length > 0

  // Reset payload for task-to-upsert on modal close
  const handleClose = () => {
    setTitle('')
    setDescription('')
    setCategory(GoalCategory.None)
    setOwnerId(null)
    setStartDate(null)
    setEndDate(null)
    onClose()
  }

  const dispatchCreateGoalSubmitted = () => {
    if (goal) {
      analytics.trackActionCreator(initiatives.Create_Goal_Submitted)({
        goalCreatedDate: goal.createdAt,
        goalUpdatedDate: goal.updatedAt,
        goalTitle: goal.title,
      })
    }
  }

  const dispatchShareGoalWithFunderChecked = () => {
    analytics.trackActionCreator(goals.Goal_Share_Funder_Toggle_Clicked)({
      goalId: goal?.id,
      goalTitle: goal?.title,
      goalUpdatedDate: goal?.updatedAt,
      isGoalSharedWithFunder: !isSharedWithFunder,
      platformPlan,
      relationship: nonprofitOrgRole,
      userAccountType: role,
    })
  }

  const handleCreateGoal = async () => {
    performance.mark('goal form submit')
    if (onCreateGoal) {
      await onCreateGoal({
        category,
        description: description || null,
        endDate: endDate ? endDate.toISOString() : null,
        impactCategory,
        nonprofitOrgId,
        ownerId,
        startDate: startDate ? startDate.toISOString() : null,
        title,
        type: GoalType.UserCustom,
        shareWithFunderOrgIds:
          isSharedWithFunder && hasActiveFunders ? activeFunders : [],
        isGoalSharedByNonprofit:
          isSharedWithFunder && hasActiveFunders ? true : undefined,
      })
    }
    dispatchCreateGoalSubmitted()
  }

  const handleUpdateGoal = async () => {
    if (onUpdateGoal && goal) {
      await onUpdateGoal({
        id: goal.id,
        category,
        description: description || null,
        endDate: endDate ? endDate.toISOString() : null,
        impactCategory,
        ownerId,
        startDate: startDate ? startDate.toISOString() : null,
        title,
        shareWithFunderOrgIds:
          isSharedWithFunder && hasActiveFunders ? activeFunders : [],
        isGoalSharedByNonprofit:
          isSharedWithFunder && hasActiveFunders ? true : undefined,
      })
    }
  }

  const onCheckboxChangeHandler = async () => {
    setIsSharedWithFunder(!isSharedWithFunder)
    dispatchShareGoalWithFunderChecked()
  }

  const categoryOptions = Object.entries(categories)
    .filter(([cat]) => cat !== GoalCategory.None)
    .map(([cat, displayValue]) => (
      <MenuItem key={cat} value={cat}>
        {displayValue}
      </MenuItem>
    ))

  const ownerOptions = teamOptions.map((option) => (
    <MenuItem key={option.value} value={option.value}>
      {option.label}
    </MenuItem>
  ))

  const isValid =
    title && category !== GoalCategory.None && impactCategory !== null

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
      disableEnforceFocus
    >
      <GoalModalContainer>
        <Header>{goal ? 'Edit Goal' : 'New Goal'}</Header>
        <Inputs>
          <TextField
            id='goalModalInputName'
            label='Name this goal'
            variant='outlined'
            size='small'
            value={domPurify.sanitize(title)}
            onChange={({ target: { value } }) => setTitle(value)}
            required
          />
          <FormControl>
            <FormLabel>
              What type of impact are you expecting to make with this goal?*
            </FormLabel>
            <RadioGroup
              name='impactCategory'
              value={impactCategory}
              onChange={({ target: { value } }) => {
                if (
                  ((val: string): val is GoalImpactCategory =>
                    (Object.values(GoalImpactCategory) as string[]).includes(
                      val
                    ))(value)
                ) {
                  setImpactCategory(value)
                }
              }}
            >
              <FormControlLabel
                value={GoalImpactCategory.Mission}
                control={<Radio size='small' />}
                label={impactCategories[GoalImpactCategory.Mission]}
                sublabel={
                  <>
                    {`This goal has a direct impact on advancing your org's mission in the community.`}
                    <br />
                    <em>Ex: Secure housing for 200 people</em>
                  </>
                }
              />
              <FormControlLabel
                value={GoalImpactCategory.Organizational}
                control={<Radio size='small' />}
                label={impactCategories[GoalImpactCategory.Organizational]}
                sublabel={
                  <>
                    This goal will help you strengthen the internal core of your
                    organization.
                    <br />
                    <em>Ex: Secure new general operating funds</em>
                  </>
                }
              />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <Select
              id='goalModalInputSelectCategory'
              displayEmpty
              value={category}
              onChange={({ target: { value } }) =>
                setCategory(value as GoalCategory)
              }
              onClose={() => setIsCategorySelectOpen(false)}
              onOpen={() => setIsCategorySelectOpen(true)}
              open={isCategorySelectOpen}
              sx={{ height: '40px' }}
              required
            >
              <MenuItem value={GoalCategory.None}>
                <CategorySelector>Select a category *</CategorySelector>
              </MenuItem>
              {categoryOptions}
            </Select>
          </FormControl>
          <TextField
            id='goalModalInputDescription'
            label='Add a brief description to your goal'
            variant='outlined'
            size='small'
            value={domPurify.sanitize(description)}
            onChange={({ target: { value } }) => setDescription(value)}
          />
          <FormControl>
            <Select
              id='goalModalInputSelectOwner'
              displayEmpty
              value={ownerId ?? 0}
              onChange={({ target: { value } }) => {
                setOwnerId(parseInt(String(value), 10))
              }}
              onClose={() => setIsOwnerSelectOpen(false)}
              onOpen={() => setIsOwnerSelectOpen(true)}
              open={isOwnerSelectOpen}
              sx={{ height: '40px' }}
            >
              <MenuItem value={0}>Assign an Owner (Optional)</MenuItem>
              {ownerOptions}
            </Select>
          </FormControl>
          <DatePickers>
            <DatePicker
              date={startDate ? startDate.format(GOAL_DATE_FORMAT) : null}
              label='Start date (Optional)'
              onDateSelected={setStartDate}
              styles={{ width: '50%' }}
            />
            <DatePicker
              date={endDate ? endDate.format(GOAL_DATE_FORMAT) : null}
              label='End date (Optional)'
              onDateSelected={setEndDate}
              styles={{ width: '50%' }}
            />
          </DatePickers>
          {hasActiveFunders && (
            <>
              <FunderCheckBox>
                <FormControlLabel
                  label={
                    <ShareFunderText>
                      Share goal with all funder(s)
                    </ShareFunderText>
                  }
                  control={
                    <Checkbox
                      defaultChecked={isSharedWithFunder}
                      color='primary'
                      onChange={onCheckboxChangeHandler}
                    />
                  }
                />
                <Tooltip
                  title={
                    <TooltipBox>
                      <TooltipText>
                        By checking this box, you are allowing us to share
                        progress of this goal with all your funders on their own
                        view of Goals via the funder platform. If you change
                        your mind, you can easily uncheck this option while
                        editing your goal and we will stop sharing this
                        information with them.
                      </TooltipText>
                    </TooltipBox>
                  }
                  arrow
                  placement='right'
                >
                  <HelpOutlinedIcon color='primary' />
                </Tooltip>
              </FunderCheckBox>
              {goal && funderSharedGoal && !isSharedWithFunder && (
                <UnshareWarning>
                  <WarningAmberIcon sx={{ marginRight: '10px' }} />
                  After saving your changes, progress on this goal will no
                  longer be shared with your funder(s).
                </UnshareWarning>
              )}
            </>
          )}
        </Inputs>
        <Buttons>
          <Button
            label='Cancel'
            onClick={handleClose}
            type='button'
            variant='tertiary'
          />
          <Button
            label={goal ? 'Save' : 'Create'}
            onClick={goal ? handleUpdateGoal : handleCreateGoal}
            type='button'
            variant='primary'
            isDisabled={!isValid}
          />
        </Buttons>
      </GoalModalContainer>
    </Modal>
  )
}
